import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PRODUCTS = [
  {
    id: "individual",
    name: "Individual",
    price: "£119",
    description: "Single inventory access",
    features: [
      "1 assessment token",
      "Complete inventory assessment",
      "Personalised partner report",
      "Email delivery of results",
    ],
    popular: false,
  },
  {
    id: "bundle_6",
    name: "Practice Bundle",
    price: "£600",
    description: "For small partnerships",
    features: [
      "6 individual tokens",
      "Share with team members",
      "Each person gets unique code",
      "All reports emailed individually",
      "Save £114 vs individual",
    ],
    popular: true,
  },
  {
    id: "bundle_10",
    name: "Practice Plus",
    price: "£1,000",
    description: "For larger partnerships",
    features: [
      "10 individual tokens",
      "Share with entire team",
      "Each person gets unique code",
      "All reports emailed individually",
      "Save £190 vs individual",
    ],
    popular: false,
  },
];

const Purchase = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled");
  
  const [email, setEmail] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handlePurchase = async (productId: string) => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSelectedProduct(productId);
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: { product: productId, email },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error("Failed to create checkout session. Please try again.");
      setLoading(false);
      setSelectedProduct(null);
    }
  };

  return (
    <Layout>
      <section className="section-padding bg-background">
        <div className="container-wide">
          {/* Hero */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Access the GP Partnership Inventory
            </h1>
            <p className="text-lg text-muted-foreground">
              Gain valuable insights into your partnership leadership style, motivations, 
              and contribution patterns with our comprehensive assessment.
            </p>
          </div>

          {/* Cancelled notice */}
          {cancelled && (
            <div className="max-w-md mx-auto mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Payment cancelled</p>
                <p className="text-sm text-amber-700">No charges were made. You can try again when you're ready.</p>
              </div>
            </div>
          )}

          {/* Email input */}
          <div className="max-w-md mx-auto mb-12">
            <Label htmlFor="email" className="text-base font-medium">
              Your email address
            </Label>
            <p className="text-sm text-muted-foreground mb-2">
              We'll send your access code(s) to this email after purchase.
            </p>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="text-lg"
            />
          </div>

          {/* Pricing cards */}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRODUCTS.map((product) => (
              <Card 
                key={product.id}
                className={`relative ${
                  product.popular 
                    ? "border-primary shadow-lg scale-105" 
                    : "border-border"
                }`}
              >
                {product.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                  <div className="pt-4">
                    <span className="text-4xl font-bold text-foreground">{product.price}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    onClick={() => handlePurchase(product.id)}
                    disabled={loading || !email}
                    className="w-full"
                    variant={product.popular ? "default" : "outline"}
                  >
                    {loading && selectedProduct === product.id ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      "Buy Now"
                    )}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Info section */}
          <div className="max-w-2xl mx-auto mt-16 text-center">
            <h2 className="text-2xl font-semibold mb-4">How It Works</h2>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div>
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold mb-3">1</div>
                <h3 className="font-medium mb-1">Purchase</h3>
                <p className="text-sm text-muted-foreground">Complete your payment securely via Stripe</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold mb-3">2</div>
                <h3 className="font-medium mb-1">Receive Code(s)</h3>
                <p className="text-sm text-muted-foreground">Get your unique access code(s) via email</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold mb-3">3</div>
                <h3 className="font-medium mb-1">Take Inventory</h3>
                <p className="text-sm text-muted-foreground">Complete the assessment and receive your report</p>
              </div>
            </div>
          </div>

          {/* Already have a code */}
          <div className="max-w-md mx-auto mt-12 text-center p-6 bg-muted/50 rounded-lg">
            <p className="text-muted-foreground mb-3">Already have an access code?</p>
            <Button variant="outline" onClick={() => navigate("/start-inventory")}>
              Enter Your Code
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Purchase;
